/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  addLineItems,
  checkout,
  getCart,
  onBeforeCheckout,
  updateCart,
  updateLineItem,
} from '@/cart/cart-store';
import {
  Cart,
  LineItemInput,
  UpdateCartInput,
  UpdateLineItemInput,
} from '@/cart/types';
import GiftWithPurchase, {
  type CartService,
} from '@/gift-with-purchase/gift-with-purchase';
import {logError} from '@/utilities/log';
import {effect} from '@preact/signals';

export type BuyStack11Props = {
  step?: number;
  bundle?: number;
  selectedSellingPlan?: number | null;
  cart: CartHandler;
  bundles: Bundle[];
};

export type Bundle = {
  minimum_quantity?: number | null;
  gift_bundle_variant?: string | null;
  require_variants?: number[] | null;
};

type CartHandler = CartService & {
  getCart: typeof getCart;
  addLineItems: typeof addLineItems;
  updateLineItem: typeof updateLineItem;
  checkout: typeof checkout;
  updateCart: typeof updateCart;
  onBeforeCheckout: typeof onBeforeCheckout;
};

const buyStack11 = ({
  step = 1,
  bundle = 0,
  bundles,
  cart,
  selectedSellingPlan = null,
}: BuyStack11Props) => ({
  step,

  bundle,

  selectedSellingPlan,

  pendingCheckout: false,

  pendingUpdate: false,

  allowCheckout: false,

  _gwp: new GiftWithPurchase({
    cart,
    minQuantity: bundles[bundle]?.minimum_quantity,
    variants: bundles[bundle]?.require_variants,
    giftId: Number(bundles[bundle]?.gift_bundle_variant?.split('/').pop()),
  }),

  init() {
    const cb = async () => {
      await this.updateSellingPlan(this.selectedSellingPlan);
    };
    cart.onBeforeCheckout(cb);
    const gwp = this.getUpdatedGwp() as GiftWithPurchase;
    gwp.listenToCart();
    effect(() => {
      this.allowCheckout = gwp.allowCheckout;
      this.pendingUpdate = gwp.isPending;
    });
  },

  getUpdatedGwp(): GiftWithPurchase {
    const gwp = this._gwp as GiftWithPurchase;
    gwp.setOptions({
      giftId: Number(
        bundles[this.bundle]?.gift_bundle_variant?.split('/').pop()
      ),
      variants: bundles[this.bundle]?.require_variants,
      minQuantity: bundles[this.bundle]?.minimum_quantity,
    });
    return gwp;
  },

  get qualifiedBundle(): number {
    const cart = this.$store.cartStore.cart as Cart | null;
    if (!cart) return this.bundle as number;

    return handleQualifiedBundle(cart, bundles, this.bundle as number);
  },

  async setStep(value: number): Promise<void> {
    if (document.startViewTransition) {
      await document.startViewTransition?.().ready;
    }

    this.step = value;
    window.scrollTo({top: 0});
    await this.updateSellingPlan(this.selectedSellingPlan);
  },

  setBundle(value: number): void {
    this.bundle = value;
    this.getUpdatedGwp();
    void this.updateSellingPlan(this.selectedSellingPlan);
  },

  async checkout() {
    try {
      const gwp = this.getUpdatedGwp() as GiftWithPurchase;
      if (!gwp.allowCheckout) {
        return;
      }

      this.pendingCheckout = true;
      await this.updateSellingPlan(this.selectedSellingPlan);
      await cart.checkout();
      setTimeout(() => (this.pendingCheckout = false), 2000);
    } catch (error) {
      logError(error, {message: 'BS_11FailedCheckout'});
    }
  },

  async incrementQuantity(variantId: number, sellingPlanId?: number) {
    await cart.addLineItems([
      {
        id: variantId,
        quantity: 1,
        selling_plan: sellingPlanId,
      },
    ]);

    await this.updateSellingPlan(this.selectedSellingPlan);
  },

  async decrementQuantity(variantId: number) {
    const cartState = cart.getCart();

    if (!cartState) return;

    const lineUpdates = handleDecrement(cartState, variantId);
    await Promise.all(lineUpdates.map((line) => cart.updateLineItem(line)));

    await this.updateSellingPlan(this.selectedSellingPlan);
  },

  async updateSellingPlan(sellingPlanId: number | null) {
    this.selectedSellingPlan = sellingPlanId;

    const cartState = cart.getCart();

    if (!cartState) return;

    try {
      const updates = handleUpdateSellingPlan(
        cartState,
        bundles,
        sellingPlanId
      );

      if (updates.length === 0) return;

      await Promise.all(updates.map((u) => cart.updateLineItem(u)));
    } catch (error) {
      logError(error, {message: 'BS_11: Error udpating selling plan'});
    }
  },
});

export function handleDecrement(
  cart: Cart,
  variantId: number
): UpdateLineItemInput[] {
  const variantIndex = cart.items.findIndex((item) => item.id === variantId);

  const items: UpdateLineItemInput[] = [];

  if (variantIndex > -1) {
    const item = cart.items[variantIndex];
    items.push({
      id: item.id.toString(),
      quantity: item.quantity - 1,
    });
  }

  return items;
}

export function handleAddGift(
  cart: Cart,
  bundle?: Bundle
): LineItemInput | null {
  if (!bundle) return null;

  const giftId = bundle.gift_bundle_variant?.split('/').pop();

  if (!giftId) return null;

  const requireVariants = bundle.require_variants;

  if (!requireVariants) return null;

  const totalQuantity = cart.items
    .filter(({id}) => requireVariants.includes(id))
    .reduce((sum, {quantity}) => sum + quantity, 0);

  const minQuantity = bundle.minimum_quantity ?? 0;

  if (minQuantity > totalQuantity) return null;

  return {
    id: Number(giftId),
    quantity: 1,
  };
}

function getRequireVariantIds(bundles: Bundle[]): Set<number> {
  const variantIds = new Set<number>();

  for (const {require_variants} of bundles) {
    if (!require_variants) continue;

    for (const id of require_variants) {
      variantIds.add(id);
    }
  }

  return variantIds;
}

export function handleUpdateSellingPlan(
  cart: Cart,
  bundles: Bundle[],
  sellingPlanId: number | null
): UpdateLineItemInput[] {
  const items: UpdateLineItemInput[] = [];

  const variantIds = getRequireVariantIds(bundles);

  for (const item of cart.items) {
    if (!variantIds.has(item.id)) {
      continue;
    }

    const planId = item.selling_plan_allocation?.selling_plan.id;

    if (!sellingPlanId && !planId) {
      continue;
    }

    if (sellingPlanId === planId) {
      continue;
    }

    items.push({
      id: item.id.toString(),
      selling_plan: sellingPlanId,
      quantity: item.quantity,
    });
  }

  return items;
}

export function handleQualifiedBundle(
  cart: Cart,
  bundles: Bundle[],
  selectedBundle: number
): number {
  const variantIds = getRequireVariantIds(bundles);

  const totalQty = cart.items
    .filter(({id}) => variantIds.has(id))
    .reduce((sum, {quantity}) => sum + quantity, 0);

  const selectedMinQty = bundles[selectedBundle].minimum_quantity ?? 1;

  let qualifiedIndex = selectedBundle;
  let maxQualifiedMinQty = -1;

  for (let i = 0; i < bundles.length; i++) {
    const minQty = bundles[i].minimum_quantity ?? 1;
    if (minQty < selectedMinQty) continue;
    if (totalQty >= minQty && minQty > maxQualifiedMinQty) {
      qualifiedIndex = i;
      maxQualifiedMinQty = minQty;
    }
  }

  return qualifiedIndex;
}

/**
 * Inspect the cart and remove any existing bundle products.
 * This is to prevent customers from accidentally checking out with multiple gift bundles.
 * Updates may be applied using the `updateCart` cart store action
 * @param cart - the current cart state
 * @returns line item updates to remove any bundle items
 */
export function handleClearBundles(cart: Cart): UpdateCartInput | null {
  const updates = cart.items
    .filter((item) => item.has_components)
    .map((item) => [item.id.toString(), 0] as const);

  return updates.length
    ? {
        updates: Object.fromEntries(updates),
      }
    : null;
}

export default buyStack11;
